/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldAlert, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Boundary Violations section — Part 4 P1 sweep visibility.
 * --------------------------------------------------------
 * Read-only listing of state-machine boundary events emitted by the route-layer
 * guards (E1 CRM, E2 Lifecycle, E3 mark-ready, E13 care_state, E15 task completion).
 *
 * Source: GET /api/governance/boundary-violations
 * Admin-only.
 */

const VIOLATION_LABELS = {
  crm_pipeline_regression: 'CRM Pipeline Regression',
  lifecycle_regression: 'Lifecycle Regression',
  mark_ready_precondition: 'Mark-Ready Precondition',
  care_state_manual_override: 'Care State Manual Override',
  orchestration_task_completion: 'Decision Card Completion',
  protocol_publish_without_decode: 'Protocol Publish without DECODE',
  support_ticket_resolution_without_staff_message: 'Ticket Resolved without Staff Message',
  suggested_action_vs_auto_action_conflict: 'Suggestion vs Auto-Action Conflict',
};

const TONE = {
  boundary_violation_blocked: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  boundary_override:          'bg-amber-500/15 text-amber-300 border-amber-500/30',
};

export default forwardRef(function BoundaryViolationsSection(_props, ref) {
  const [data, setData] = useState({ events: [], by_violation_type: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ days: '30', violation_type: '__all__', kind: '__all__' });
  const [expanded, setExpanded] = useState({});
  const sectionRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days: Number(filters.days) };
      if (filters.violation_type !== '__all__') params.violation_type = filters.violation_type;
      if (filters.kind !== '__all__') params.kind = filters.kind;
      const r = await axios.get(`${API}/governance/boundary-violations`, { params });
      setData(r.data || { events: [], by_violation_type: {}, total: 0 });
    } catch {
      setData({ events: [], by_violation_type: {}, total: 0, error: true });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Imperative handle so external siblings (e.g. GovernanceHotSpotsTile) can
  // deep-link into a filtered view + scroll the section into focus.
  useImperativeHandle(ref, () => ({
    applyFilter: ({ violation_type, days }) => {
      setFilters((p) => ({
        ...p,
        violation_type: violation_type || '__all__',
        days: days != null ? String(days) : p.days,
        kind: '__all__',
      }));
      // Scroll AFTER state commits / re-render.
      setTimeout(() => {
        if (sectionRef.current?.scrollIntoView) {
          sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
    },
  }), []);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  return (
    <section className="mb-12" ref={sectionRef} id="boundary-violations" data-testid="boundary-violations-section">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-medium text-white flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-rose-300" />
            Boundary Violations
            <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/30 text-[10px]">P4</Badge>
          </h2>
          <p className="text-xs text-white/50 mt-0.5">
            Route-layer guard events: blocked attempts and admin overrides across CRM, lifecycle, mark-ready, care_state, and decision cards.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="w-32">
            <label className="text-[10px] uppercase tracking-wider text-white/50">Window</label>
            <Select value={filters.days} onValueChange={(v) => setFilters({ ...filters, days: v })}>
              <SelectTrigger data-testid="bv-filter-days"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">180 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-52">
            <label className="text-[10px] uppercase tracking-wider text-white/50">Type</label>
            <Select value={filters.violation_type} onValueChange={(v) => setFilters({ ...filters, violation_type: v })}>
              <SelectTrigger data-testid="bv-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All types</SelectItem>
                {Object.entries(VIOLATION_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <label className="text-[10px] uppercase tracking-wider text-white/50">Kind</label>
            <Select value={filters.kind} onValueChange={(v) => setFilters({ ...filters, kind: v })}>
              <SelectTrigger data-testid="bv-filter-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                <SelectItem value="boundary_violation_blocked">Blocked</SelectItem>
                <SelectItem value="boundary_override">Override</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}
            className="border-white/15 text-white/70 hover:bg-white/5"
            data-testid="bv-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Counts strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="bv-counts">
        {Object.entries(VIOLATION_LABELS).map(([k, label]) => {
          const c = data.by_violation_type?.[k] || { blocked: 0, override: 0, total: 0 };
          return (
            <div key={k} className="rounded border border-white/10 bg-adm-surface/5 p-2"
              data-testid={`bv-count-${k}`}>
              <div className="text-[10px] uppercase tracking-wider text-white/50 truncate">{label}</div>
              <div className="text-sm text-white mt-0.5 tabular-nums">
                {c.total} <span className="text-[10px] text-white/40">({c.blocked} blocked / {c.override} override)</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Events list */}
      <div className="space-y-1.5" data-testid="bv-events">
        {data.events.length === 0 && !loading && (
          <div className="text-sm text-white/40 italic px-2">No boundary events in this window.</div>
        )}
        {data.events.map((e) => {
          const id = e.id || `${e.timestamp}-${e.entity_id}`;
          const open = !!expanded[id];
          return (
            <div key={id} className="rounded border border-white/10 bg-adm-surface/5"
              data-testid={`bv-row-${id}`}>
              <button type="button" onClick={() => toggle(id)}
                className="w-full text-left flex items-start justify-between gap-3 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`text-[10px] ${TONE[e.action] || ''}`}>
                      {e.action === 'boundary_violation_blocked' ? 'BLOCKED' : 'OVERRIDE'}
                    </Badge>
                    <span className="text-sm text-white">{VIOLATION_LABELS[e.violation_type] || e.violation_type}</span>
                    <Badge className="bg-slate-700/40 text-slate-300 border-slate-600 text-[10px]">
                      {e.from_state || '—'} → {e.to_state || '—'}
                    </Badge>
                  </div>
                  <div className="text-[11px] text-white/50 mt-0.5">
                    {new Date(e.timestamp).toLocaleString()} · {e.actor_role}/{e.actor_id} · {e.entity_type} {e.entity_id}
                    {e.subscriber_id && <> · subscriber {e.subscriber_id}</>}
                  </div>
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
              </button>
              {open && (
                <div className="px-3 pb-3">
                  {e.reason && (
                    <div className="text-xs text-white/70 italic mb-1">Reason: {e.reason}</div>
                  )}
                  {e.extra && Object.keys(e.extra).length > 0 && (
                    <pre className="text-[11px] text-white/60 bg-black/30 rounded p-2 overflow-x-auto">
{JSON.stringify(e.extra, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
});
